import { useState, useRef, useEffect, useMemo } from "react";
import { X, Dog, Cat } from "lucide-react";
import VaccinesForm from "../client_pets/PetsVaccines";
import Appointments from "../client_pets/PetsAppointments";
import SelectAddOnsDrawer from "./SelectAddOnsDrawer";
import ClientSearchPetMedicalDrawer from "../client_pets/Drawers/ClientSearchPetMedicaldrawer";
import PetServiceCategoryDrawer from "../Orders/PetServiceCategoryDrawer";
import AddAppointment from "../Calendar/BigCalendar/appointmentcalendar/AddAppointment";
import { useProfile } from "../../context/ProfileContext";
import { httpService } from "../../services/httpService";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";

const PetDetailsDrawer = ({
  petData,
  isOpen,
  onClose,
  onRefresh, // Add this new prop
  clientAppointments = [],
  clientBoarding = [],
  clientData = {},
  triggergetclientapi = false,
}) => {
  const {
    name = "",
    age,
    breed,
    pet_type,
    weight,
    weight_kg,
    pet_weight,
    body_weight,
    wt,
    gender,
    tags = [],
    date_of_birth,
    birthday,
    color,
    microchip_id,
    registration_number,
    vaccination_status,
    last_vet_visit,
    medical_notes,
    behavior_notes,
    neutered_spayed,
  } = petData || {};

   const { isPlanExpired, rawServiceCategory } = useProfile();

   const isPetShopOrPosOnly = useMemo(() => {
     if (!rawServiceCategory || rawServiceCategory.length === 0) return false;

     const categoryNames = rawServiceCategory
       .map((cat) =>
         (cat.service_category_name || cat.name || "").toLowerCase(),
       )
       .filter(Boolean);

     const uniqueCategories = [...new Set(categoryNames)];

     return (
       uniqueCategories.length === 1 &&
       (uniqueCategories[0] === "pet shop" || uniqueCategories[0] === "pos")
     );
   }, [rawServiceCategory]);

  const [allOrders, setAllOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [petDetailsData, setPetDetailsData] = useState(null);

  // Try multiple weight field names
  const finalWeight = weight_kg || weight || pet_weight || body_weight || wt;

  const [petImage, setPetImage] = useState(petData?.image || null);
  const [activeTab, setActiveTab] = useState(
    isPetShopOrPosOnly ? "medical" : "schedule",
  );
  const fileInputRef = useRef(null);
  const drawerRef = useRef(null);

  const [isAddonsDrawerOpen, setIsAddonsDrawerOpen] = useState(false);
  const [selectedServiceForAddons, setSelectedServiceForAddons] =
    useState(null);
  const [selectedServiceIndex, setSelectedServiceIndex] = useState(null);
  const [showPetServiceDrawer, setShowPetServiceDrawer] = useState(false);
  const [editingService, setEditingService] = useState(null);

  const [showAddAppointmentDrawer, setShowAddAppointmentDrawer] =
    useState(false);
  const [appointmentClientDetails, setAppointmentClientDetails] =
    useState(null);

  const petType = pet_type?.name?.toLowerCase() || "pet";
  const breedName = breed?.breed_name || "Unknown breed";
  const formattedAge = age ? `${age} ${age === 1 ? "year" : "years"}` : "";
  const formattedGender = gender
    ? gender.charAt(0).toUpperCase() + gender.slice(1)
    : "";
  const formattedWeight = finalWeight ? `${finalWeight} kg` : "";
 

  useEffect(() => {
    if (triggergetclientapi && petData?.vendor_client_id) {
      const fetchClientOverview = async () => {
        setIsLoading(true);
        try {
          const response = await httpService.get(
            `/vendorclient/getVendorClientPetsOverviewById/${petData.vendor_client_id}`,
          );
          // Extract order_details from the response
          const orders = response?.data?.data?.order_details || [];
          setAllOrders(orders);
          setPetDetailsData(
            response?.data?.data?.pet_details?.find(
              (p) => p._id === petData._id,
            ),
          );
        } catch (error) {
          console.error("Failed to fetch client overview:", error);
          setAllOrders([]);
        } finally {
          setIsLoading(false);
        }
      };
      fetchClientOverview();
    }
  }, [petData?.vendor_client_id, triggergetclientapi]);

  // Handle drawer close with refresh
  const handleClose = () => {
    onClose();
    // Call refresh after drawer closes
    if (onRefresh) {
      onRefresh();
    }
  };

  const prepareClientDetails = () => {
    return {
      _id: clientData?._id,
      firstname: clientData?.firstname || "",
      lastname: clientData?.lastname || "",
      phone: clientData?.phone || "",
      email: clientData?.email || "",
      address: clientData?.address || {},
      pet_details: [
        {
          _id: petData?._id,
          name: petData?.name,
          pet_type: petData?.pet_type,
          breed: petData?.breed,
          size: petData?.size_details,
          _selected: true,
        },
      ],
    };
  };

  const getPetIcon = () => {
    if (petType.includes("cat")) {
      return <Cat className="w-12 h-12 text-gray-600" strokeWidth={1.5} />;
    }
    return <Dog className="w-12 h-12 text-gray-600" strokeWidth={1.5} />;
  };

  const handleOpenAddons = (service, index) => {
    setSelectedServiceForAddons(service);
    setSelectedServiceIndex(index);
    setIsAddonsDrawerOpen(true);
  };

  const handleSaveAddons = (updatedAddons) => {
    setIsAddonsDrawerOpen(false);
    setSelectedServiceForAddons(null);
    setSelectedServiceIndex(null);
  };

  const handleEditAppointment = (serviceData, index) => {
    const formatDateForInput = (dateString) => {
      if (!dateString) return "";
      try {
        let date;
        if (dateString.includes("-")) {
          const [day, month, year] = dateString.split("-");
          date = new Date(`${year}-${month}-${day}`);
        } else {
          date = new Date(dateString);
        }

        const yearFormatted = date.getFullYear();
        const monthFormatted = String(date.getMonth() + 1).padStart(2, "0");
        const dayFormatted = String(date.getDate()).padStart(2, "0");

        return `${yearFormatted}-${monthFormatted}-${dayFormatted}`;
      } catch (error) {
        console.error("Error formatting date:", error);
        return "";
      }
    };

    const appointmentDate =
      serviceData.start_date ||
      serviceData.appointment_date ||
      serviceData.startDate;
    const timeSlot =
      serviceData.start_time ||
      serviceData.appointment_time ||
      serviceData.startTime;
    const formattedDate = formatDateForInput(appointmentDate);

    const editData = {
      vendor_client_id: petData?.vendor_client_id,
      vendor_pet_id: petData?._id,
      pet_id: petData?._id,
      pet_name: petData?.name,
      pet_type: petData?.pet_type,
      pet_breed: petData?.breed?.breed_name,
      pet_size: petData?.size_details?.size_label,
      service_id: serviceData.service_id,
      vendor_service_id: serviceData.vendor_service_id,
      service_category_type_id: serviceData.service_category_type_id,
      service_type_name:
        serviceData.service_type_name || serviceData.serviceTypeName,
      service_type_id: serviceData.service_type_id,
      item_type: serviceData.item_type || serviceData.type || "grooming",
      service_name: serviceData.service_name || serviceData.serviceName,
      start_date: formattedDate,
      start_time: timeSlot,
      duration: serviceData.duration,
      price: serviceData.unitPrice || serviceData.price,
      total_price: serviceData.total_price || serviceData.totalPrice,
      employee_id: serviceData.employee_id,
      employee_name: serviceData.employee_name || serviceData.employeeName,
      technician_id: serviceData.employee_id || serviceData.technician_id,
      appointment_date: formattedDate,
      appointment_time: timeSlot,
      ...serviceData,
      order_number: serviceData.order_number,
      ref_id: serviceData.ref_id || serviceData.refId,
      item_id: serviceData.item_id || serviceData.itemId,
      addons: serviceData.addons || [],
      _isEdit: true,
      _editIndex: index,
      _originalAppointment: { ...serviceData },
      _hasValidServiceId: !!serviceData.vendor_service_id,
    };

    setEditingService(editData);
    setShowPetServiceDrawer(true);
  };

  const handleServiceDataSaved = (data) => {
    setShowPetServiceDrawer(false);
    setEditingService(null);
  };

  const handleAppointmentSaved = () => {
    setShowAddAppointmentDrawer(false);
    setAppointmentClientDetails(null);
  };

  const handleAppointmentClose = () => {
    setShowAddAppointmentDrawer(false);
    setAppointmentClientDetails(null);
  };

  useEffect(() => {
    if (!showPetServiceDrawer) {
      setEditingService(null);
    }
  }, [showPetServiceDrawer]);

  const tabs = isPetShopOrPosOnly
    ? [{ id: "medical", label: " Recent Visits" }]
    : [
        { id: "schedule", label: "Upcoming Appointments" },
        { id: "medical", label: " Recent Visits" },
        { id: "vaccines", label: "Vaccines" },
      ];

      useEffect(() => {
        if (isPetShopOrPosOnly && activeTab !== "medical") {
          setActiveTab("medical");
        }
      }, [isPetShopOrPosOnly]);

  const getFooterButtonLabel = () => {
    switch (activeTab) {
      case "schedule":
        return "+ Add Appointment";
      case "vaccines":
        return "";
      case "medical":
        return "";
      default:
        return "";
    }
  };

  const handleFooterButtonClick = () => {
    switch (activeTab) {
      case "schedule":
        setAppointmentClientDetails(prepareClientDetails());
        setShowAddAppointmentDrawer(true);
        break;
      case "vaccines":
        break;
      case "medical":
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on portal elements by looking at the portal container
      const isPortalClick =
        event.target.closest("[data-portal]") ||
        event.target.closest(".dropdown-menu") ||
        // Check if element is rendered via ReactDOM.createPortal
        // Portals are attached to body, so check if parent is body
        event.target.parentNode === document.body ||
        event.target.parentNode?.parentNode === document.body;

      // Also check if it's the status dropdown backdrop
      const isBackdropClick =
        event.target.classList.contains("fixed") &&
        event.target.classList.contains("inset-0") &&
        event.target.classList.contains("z-[9998]");

      if (
        drawerRef.current &&
        !drawerRef.current.contains(event.target) &&
        !event.target.closest(".pet-service-drawer") &&
        !isAddonsDrawerOpen &&
        !showPetServiceDrawer &&
        !showAddAppointmentDrawer &&
        !isPortalClick && // Add this check
        !isBackdropClick
      ) {
        // And this
        handleClose(); // Use handleClose instead of onClose
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "unset";
    };
  }, [
    isOpen,
    handleClose, // Add handleClose to dependencies
    isAddonsDrawerOpen,
    showPetServiceDrawer,
    showAddAppointmentDrawer,
  ]);
  useEffect(() => {
    if (!isOpen) {
      setPetDetailsData(null);
    }
  }, [isOpen]);

  const handleImageClick = () => fileInputRef.current?.click();

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setPetImage(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "vaccines":
        return (
          <VaccinesForm
            pet={petDetailsData ? petDetailsData : petData}
            vendorId={petData?.vendor_id}
          />
        );

      case "medical":
        return (
          <Appointments
            mode="history"
            petData={petDetailsData || petData}
            clientAppointments={clientAppointments}
            clientBoarding={clientBoarding}
            clientOrders={allOrders}
            onEditAppointment={handleEditAppointment}
            onOpenAddons={handleOpenAddons}
            setEditingService={setEditingService}
            setShowPetServiceDrawer={setShowPetServiceDrawer}
          />
        );
      case "schedule":
        return (
          <Appointments
            mode="upcoming"
            petData={petDetailsData ? petDetailsData : petData}
            clientAppointments={clientAppointments}
            clientBoarding={clientBoarding}
            clientOrders={allOrders}
            onEditAppointment={handleEditAppointment}
            onOpenAddons={handleOpenAddons}
            setEditingService={setEditingService}
            setShowPetServiceDrawer={setShowPetServiceDrawer}
          />
        );
      default:
        return (
          <VaccinesForm
            petId={
              petDetailsData.length > 0 && petDetailsData
                ? petDetailsData
                : petData
            }
            vendorId={petData.vendor_id}
          />
        );
    }
  };

  if (!isOpen) return null;
  if (isLoading) return <LoaderSpiner text="Loading . . ." />;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex justify-end">
      <div
        ref={drawerRef}
        className="h-full w-full lg:w-2/5 bg-white shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 bg-green-50 border-b border-gray-100">
          <button
            onClick={handleClose} // Change from onClose to handleClose
            className="absolute top-4 right-4 p-1 rounded-md hover:bg-gray-100"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>

          <div className="flex items-start gap-4 mb-4">
            <div
              className="w-20 h-20 rounded-full bg-green-100 border border-green-200 overflow-hidden flex items-center justify-center cursor-pointer flex-shrink-0"
              onClick={handleImageClick}
            >
              {petImage ? (
                <img
                  src={petImage}
                  alt={name}
                  className="w-full h-full object-cover"
                />
              ) : (
                getPetIcon()
              )}
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-gray-900 truncate">
                {name}
              </h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full uppercase tracking-wide">
                  {petType}
                </span>
                {tags?.map((t, i) => (
                  <span
                    key={i}
                    className="px-2 py-1 bg-white rounded-full border text-xs text-gray-600"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Pet Information - SINGLE LINE */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {breedName && breedName !== "Unknown breed" && (
              <>
                <div className="flex items-center gap-1">
                  <span className="text-lg">🐾</span>
                  <span className="font-medium text-gray-900">{breedName}</span>
                </div>
                <span className="text-gray-400">|</span>
              </>
            )}

            {petData?.size_details?.size_label && (
              <>
                <div className="flex items-center gap-1">
                  <span className="text-lg">📏</span>
                  <span className="font-medium text-gray-900">
                    {petData.size_details.size_label}
                  </span>
                </div>
                <span className="text-gray-400">|</span>
              </>
            )}

            {formattedGender && (
              <>
                <div className="flex items-center gap-1">
                  <span className="text-lg">
                    {gender?.toLowerCase() === "male" ? "♂️" : "♀️"}
                  </span>
                  <span className="font-medium text-gray-900">
                    {formattedGender}
                  </span>
                </div>
                <span className="text-gray-400">|</span>
              </>
            )}

            {age && (
              <>
                <div className="flex items-center gap-1">
                  <span className="text-lg">📅</span>
                  <span className="font-medium text-gray-900">
                    {formattedAge}
                  </span>
                </div>
                <span className="text-gray-400">|</span>
              </>
            )}

            {formattedWeight && (
              <div className="flex items-center gap-1">
                <span className="text-lg">⚖️</span>
                <span className="font-medium text-gray-900">
                  {formattedWeight}
                </span>
              </div>
            )}
          </div>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept="image/*"
            className="hidden"
          />
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-white">
          <div className="flex">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-2 text-center text-lg font-bold transition-colors relative ${
                  activeTab === tab.id
                    ? "text-blue-600 bg-blue-50 font-semibold"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                {tab.label}
                {activeTab === tab.id && (
                  <div className="absolute left-0 right-0 bottom-0 h-1 bg-blue-600"></div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">{renderTabContent()}</div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-white p-4">
          {/* <div className="flex justify-end gap-3">
            <button
              onClick={handleClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-medium"
            >
              Close
            </button>
            {!(activeTab === "schedule" && isPlanExpired) &&
              getFooterButtonLabel() && (
                <button
                  onClick={handleFooterButtonClick}
                  className="px-6 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium"
                >
                  {getFooterButtonLabel()}
                </button>
              )}
          </div> */}
        </div>
      </div>

      {/* AddAppointment Drawer */}
      {showAddAppointmentDrawer && (
        <AddAppointment
          isOpen={showAddAppointmentDrawer}
          onClose={handleAppointmentClose}
          selectedSlotInfo={null}
          clearSlotSelection={() => {}}
          editMode={false}
          editingAppointment={null}
          onSave={handleAppointmentSaved}
          appointmentStatusRef={null}
          clientDetails={appointmentClientDetails}
          preSelectedPetId={petData?._id}
          isPetDetails={true}
        />
      )}

      {/* Select Addons Drawer */}
      {isAddonsDrawerOpen && (
        <SelectAddOnsDrawer
          isOpen={isAddonsDrawerOpen}
          onClose={() => setIsAddonsDrawerOpen(false)}
          onSave={handleSaveAddons}
          serviceCategoryId={selectedServiceForAddons?.service_category_type_id}
          petType={selectedServiceForAddons?.pet_type || "Both"}
          selectedAddons={selectedServiceForAddons?.addons || []}
          serviceTypeName={
            selectedServiceForAddons?.service_type_name ||
            selectedServiceForAddons?.service_name
          }
        />
      )}

      {/* Pet Service Category Drawer */}
      {showPetServiceDrawer && (
        <PetServiceCategoryDrawer
          isOpen={showPetServiceDrawer}
          onClose={() => {
            setShowPetServiceDrawer(false);
            setEditingService(null);
          }}
          onUpdate={handleServiceDataSaved}
          clientId={petData?.vendor_client_id}
          clientName={
            `${clientData?.firstname || ""} ${
              clientData?.lastname || ""
            }`.trim() || "No Name"
          }
          clientNumber={`${clientData.phone}`}
          itemData={
            editingService || {
              vendor_client_id: petData?.vendor_client_id,
              vendor_pet_id: petData?._id,
              pet_id: petData?._id,
              pet_name: petData?.name,
              pet_type: petData?.pet_type,
              pet_breed: petData?.breed?.breed_name,
              pet_size: petData?.size_details?.size_label,
            }
          }
          orderId={editingService?.order_number || null}
          fromCalendar={false}
          selectedSlotInfo={null}
          editMode={!!editingService}
          onRefreshData={() => {}}
          appointmentStatusRef={null}
          autoOpenPetForm={false}
          isServiceEditMode={!!editingService}
          serviceId={editingService?.service_id}
          vendorServiceId={editingService?.vendor_service_id}
          serviceCategoryTypeId={editingService?.service_category_type_id}
          serviceTypeId={editingService?.service_type_id}
          refId={editingService?.ref_id}
          serviceTypeName={editingService?.service_type_name}
          isPetDetails={true}
        />
      )}
    </div>
  );
};

export default PetDetailsDrawer;
